import type { Id, Transaction } from '@vp/shared-types';
import type { CanvasDocument } from '../document';
import type { CanvasMetaExt, StorageAdapter } from './adapter';

export type SaveStatus = 'saved' | 'saving' | 'error';

export interface AutoSaverOptions {
  debounceMs?: number;
  onStatus?: (s: SaveStatus, err?: unknown) => void;
  /** Timing callback for diagnostics (§14.4). */
  onSaved?: (ms: number, objects: number) => void;
  extraMeta?: () => Partial<CanvasMetaExt>;
}

/**
 * Batched autosave (§10.3). Committed transactions are first appended to the
 * operation log (WAL) almost immediately; object state is written in short
 * batches together with the WAL checkpoint, inside one IDB transaction.
 */
export class AutoSaver {
  private dirtyObjects = new Set<Id>();
  private dirtyLinks = new Set<Id>();
  private wal: Transaction[] = [];
  private lastSeq = 0;
  private stateTimer: ReturnType<typeof setTimeout> | null = null;
  private walScheduled = false;
  private chain: Promise<void> = Promise.resolve();
  private status: SaveStatus = 'saved';
  private retryMs = 1000;
  private unsub: () => void;
  private disposed = false;
  private metaDirty = false;

  constructor(
    private readonly doc: CanvasDocument,
    private readonly storage: StorageAdapter,
    private readonly opts: AutoSaverOptions = {},
  ) {
    this.unsub = doc.onCommit((tx, meta) => {
      for (const op of tx.ops) {
        const id = op.t === 'update' ? op.id : op.rec.id;
        (op.table === 'objects' ? this.dirtyObjects : this.dirtyLinks).add(id);
      }
      if (!meta.replay) {
        this.wal.push(tx);
        this.scheduleWal();
      }
      this.schedule();
    });
  }

  get state(): SaveStatus {
    return this.status;
  }

  get pending(): boolean {
    return this.dirtyObjects.size > 0 || this.dirtyLinks.size > 0 || this.wal.length > 0 || this.metaDirty;
  }

  /** Mark canvas metadata (e.g. camera) to be written with the next batch. */
  touchMeta(): void {
    this.metaDirty = true;
    this.schedule();
  }

  private setStatus(s: SaveStatus, err?: unknown): void {
    if (s === this.status && s !== 'error') return;
    this.status = s;
    this.opts.onStatus?.(s, err);
  }

  private scheduleWal(): void {
    if (this.walScheduled) return;
    this.walScheduled = true;
    queueMicrotask(() => {
      this.walScheduled = false;
      // a failed write already set the error status and keeps the batch for the next attempt
      this.enqueue(() => this.writeWal()).catch(() => undefined);
    });
  }

  private schedule(delay = this.opts.debounceMs ?? 300): void {
    if (this.disposed) return;
    this.setStatus('saving');
    if (this.stateTimer) return;
    this.stateTimer = setTimeout(() => {
      this.stateTimer = null;
      this.enqueue(() => this.writeState());
    }, delay);
  }

  private enqueue(fn: () => Promise<void>): Promise<void> {
    this.chain = this.chain.then(fn, fn);
    return this.chain;
  }

  private async writeWal(): Promise<void> {
    if (!this.wal.length) return;
    const batch = this.wal;
    this.wal = [];
    try {
      const seq = await this.storage.appendOps(this.doc.canvasId, batch);
      if (seq) this.lastSeq = seq;
    } catch (e) {
      this.wal = batch.concat(this.wal);
      this.setStatus('error', e);
      throw e;
    }
  }

  private async writeState(): Promise<void> {
    if (!this.pending) {
      this.setStatus('saved');
      return;
    }
    const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
    try {
      await this.writeWal();
      const objIds = [...this.dirtyObjects];
      const linkIds = [...this.dirtyLinks];
      this.dirtyObjects.clear();
      this.dirtyLinks.clear();
      this.metaDirty = false;
      const putObjects = [];
      const deleteObjects: Id[] = [];
      for (const id of objIds) {
        const o = this.doc.get(id);
        if (o) putObjects.push(o);
        else deleteObjects.push(id);
      }
      const putLinks = [];
      const deleteLinks: Id[] = [];
      for (const id of linkIds) {
        const l = this.doc.getLink(id);
        if (l) putLinks.push(l);
        else deleteLinks.push(id);
      }
      try {
        await this.storage.saveBatch({
          canvasId: this.doc.canvasId,
          putObjects,
          deleteObjects,
          putLinks,
          deleteLinks,
          appliedSeq: this.lastSeq || undefined,
          meta: { objectCount: this.doc.size, ...(this.opts.extraMeta?.() ?? {}) },
        });
      } catch (e) {
        for (const id of objIds) this.dirtyObjects.add(id);
        for (const id of linkIds) this.dirtyLinks.add(id);
        throw e;
      }
      const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
      this.opts.onSaved?.(t1 - t0, putObjects.length + deleteObjects.length);
      this.retryMs = 1000;
      if (this.pending) this.schedule();
      else this.setStatus('saved');
    } catch (e) {
      this.setStatus('error', e);
      // retry with backoff; dirty sets are kept
      const delay = this.retryMs;
      this.retryMs = Math.min(this.retryMs * 2, 30000);
      if (!this.disposed) {
        if (this.stateTimer) clearTimeout(this.stateTimer);
        this.stateTimer = setTimeout(() => {
          this.stateTimer = null;
          this.enqueue(() => this.writeState());
        }, delay);
      }
    }
  }

  /** Write everything now (tab switch, take-over, page hide). */
  async flush(): Promise<void> {
    if (this.stateTimer) {
      clearTimeout(this.stateTimer);
      this.stateTimer = null;
    }
    await this.enqueue(() => this.writeState());
  }

  async dispose(): Promise<void> {
    this.unsub();
    await this.flush();
    this.disposed = true;
    if (this.stateTimer) clearTimeout(this.stateTimer);
  }
}
